import { DateTime } from 'luxon'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { BidType, MARKET_TIMEZONE } from '@/features/bidding/constants'
import { isPtuEditable } from '@/features/bidding/utils/isPtuEditable'
import type { AggregatedBid, AggregatedBidPtu } from '@/features/bidding/utils/model/buildAggregatedBid'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import {
  exceedsAllowedPriceStepsPerPtu,
  hasConflictingPricesAcrossActivationGroups,
  isPriceAreaAggregatedPartner,
  isSinglePriceStepPartner,
} from '@/features/bidding/utils/validation/priceStepValidationUtils'

/**
 * Core validation flags returned by the validation logic
 */
export type AggregatedBidValidationFlags = {
  hasChunks: boolean
  overbiddingPtus: AggregatedBidPtu[]
  underbiddingPtus: AggregatedBidPtu[]
  /** PTUs with more than one non-zero price step inside this activation group. */
  priceStepViolationPtus: AggregatedBidPtu[]
  /** PTUs whose price disagrees with the other activation groups of a price-area-aggregated partner. */
  crossActivationGroupPriceViolationPtus: AggregatedBidPtu[]
}

/**
 * Core validation logic for aggregated bids.
 * This is the single source of truth for all validation rules.
 *
 * Returns validation flags that can be used by different consumers:
 * - UI: Convert to simple state ('valid' | 'invalid' | 'pending')
 * - Form validation: Build detailed error messages
 *
 * @param aggregatedBid - The aggregated bid to validate
 * @param activationGroup - The activation group context for the partner-specific price rules
 * @param bidsPerActivationGroup - Map of bids per activation group
 * @param bidType - The type of bid (i.e., CAPACITY, ENERGY)
 * @returns Validation flags indicating which rules passed/failed and which PTUs have issues
 */
export function validateAggregatedBidCore(
  aggregatedBid: AggregatedBid,
  activationGroup: ActivationGroup,
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
  bidType: BidType,
): AggregatedBidValidationFlags {
  const hasChunks = aggregatedBid.ptus.every((ptu) => ptu.priceQuantities.length > 0)

  const overbiddingPtus = aggregatedBid.ptus.filter((ptu) => ptu.remaining < 0)

  // PM requires an expired PTU's volume to equal its baseline offeredQuantity while only the
  // answered chunks may be re-sent (see findExpiredPtuPrefillSource), so sum(chunks) < volume is the
  // valid submission there - PM itself enforces only sum(chunks) <= volume. An expired PTU is no
  // longer editable, so it can never be corrected on this page and must not block the day's submit.
  const currentTime = DateTime.now().setZone(MARKET_TIMEZONE)
  const underbiddingPtus = aggregatedBid.ptus.filter(
    (ptu) => ptu.remaining > 0 && isPtuEditable(currentTime, ptu.ptuStart, bidType, activationGroup.marketProgram),
  )

  const { partnerCode } = activationGroup.portfolio
  const isSinglePriceStep = isSinglePriceStepPartner(partnerCode)
  // Only CAPACITY bids of price-area partners collapse into one bid.
  const enforcesSamePriceAcrossActivationGroups =
    isPriceAreaAggregatedPartner(partnerCode) && bidType === BidType.CAPACITY

  const priceStepViolationPtus: AggregatedBidPtu[] = []
  const crossActivationGroupPriceViolationPtus: AggregatedBidPtu[] = []
  for (const ptu of aggregatedBid.ptus) {
    if (isSinglePriceStep && exceedsAllowedPriceStepsPerPtu(ptu)) {
      priceStepViolationPtus.push(ptu)
    }
    if (
      enforcesSamePriceAcrossActivationGroups &&
      hasConflictingPricesAcrossActivationGroups(ptu, bidsPerActivationGroup)
    ) {
      crossActivationGroupPriceViolationPtus.push(ptu)
    }
  }

  return {
    hasChunks,
    overbiddingPtus,
    underbiddingPtus,
    priceStepViolationPtus,
    crossActivationGroupPriceViolationPtus,
  }
}

/**
 * Validates an aggregated bid and returns a simple state for UI purposes.
 *
 * Used for:
 * - Tab icons (checkmark, error, or no icon)
 * - Real-time validation feedback during editing
 *
 * @param aggregatedBid - The aggregated bid to validate
 * @param activationGroup - The activation group context
 * @param bidsPerActivationGroup - Map of bids per activation group
 * @param bidType - The type of bid (i.e., CAPACITY, ENERGY)
 * @returns Simple validation state: 'valid' | 'invalid' | 'pending'
 */
export const validateAggregatedBid = (
  aggregatedBid: AggregatedBid,
  activationGroup: ActivationGroup,
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
  bidType: BidType,
): 'valid' | 'invalid' | 'pending' => {
  const flags = validateAggregatedBidCore(aggregatedBid, activationGroup, bidsPerActivationGroup, bidType)

  if (!flags.hasChunks) return 'pending'

  const hasErrors =
    flags.overbiddingPtus.length > 0 ||
    flags.underbiddingPtus.length > 0 ||
    flags.priceStepViolationPtus.length > 0 ||
    flags.crossActivationGroupPriceViolationPtus.length > 0

  return hasErrors ? 'invalid' : 'valid'
}
